import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import { TbCaretDownFilled } from 'react-icons/tb';

import { cn } from '#/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '#/modules/commonComponents/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';

import { Button } from './Button';

function resolveDefaultItem(items, defaultItemId) {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }
  if (defaultItemId) {
    return items.find((item) => item.id === defaultItemId) ?? items[0];
  }
  return items[0];
}

/**
 * 左文字觸發 defaultItem.onSelect（不開選單）；右 caret 開 DropdownMenu（含 default）。
 * variant 只換外觀：slate＝列印標籤；default＝新增包裝（灰框 Button sm＋中間分隔）。
 * default item disabled 只停左鈕，caret 仍可開。
 */
const CompositeMenuButton = ({
  items,
  defaultItemId,
  variant = 'default',
  label,
  caretTooltip,
  caretAriaLabel = '開啟選單',
  disabled = false,
  className,
  ...rest
}) => {
  const defaultItem = useMemo(
    () => resolveDefaultItem(items, defaultItemId),
    [defaultItemId, items],
  );
  const isSlate = variant === 'slate';
  const isLeftDisabled = disabled || Boolean(defaultItem?.disabled);
  const leftLabel = label ?? defaultItem?.label ?? '';

  function handleDefaultClick() {
    if (isLeftDisabled) {
      return;
    }
    defaultItem?.onSelect?.();
  }

  if (!defaultItem) {
    return null;
  }

  const caretTrigger = (
    <DropdownMenuTrigger asChild>
      {isSlate ? (
        <button
          type="button"
          className={cn(
            'flex cursor-pointer items-center py-1 pr-1',
            'shortcut_transition',
            'disabled:cursor-not-allowed disabled:opacity-60',
          )}
          disabled={disabled}
          aria-label={caretAriaLabel}
        >
          <TbCaretDownFilled className="ml-1" size={20} />
        </button>
      ) : (
        <Button
          type="button"
          size="sm"
          className="rounded-none border-0 px-2 shadow-none"
          disabled={disabled}
          aria-label={caretAriaLabel}
        >
          <ChevronDown size={16} />
        </Button>
      )}
    </DropdownMenuTrigger>
  );

  const caretControl = caretTooltip ? (
    <Tooltip>
      <TooltipTrigger asChild>
        {caretTrigger}
      </TooltipTrigger>
      <TooltipContent side="top">{caretTooltip}</TooltipContent>
    </Tooltip>
  ) : caretTrigger;

  return (
    <div
      className={cn(
        isSlate
          ? cn(
            'group flex items-center overflow-hidden rounded-md',
            'border border-slate-500 bg-slate-500 text-slate-100',
            'hover:bg-slate-50 hover:text-slate-500',
            'has-[[data-state=open]]:bg-slate-50 has-[[data-state=open]]:text-slate-500',
          )
          : 'flex overflow-hidden rounded-md border border-gray-300',
        className,
      )}
      {...rest}
    >
      {isSlate ? (
        <button
          type="button"
          className={cn(
            'flex cursor-pointer items-center py-1 pl-2',
            'text-sm font-medium shortcut_transition',
            'disabled:cursor-not-allowed disabled:opacity-60',
          )}
          disabled={isLeftDisabled}
          onClick={handleDefaultClick}
        >
          <span className="pr-2 pb-0.5">{leftLabel}</span>
        </button>
      ) : (
        <Button
          type="button"
          size="sm"
          className="rounded-none border-0 shadow-none"
          disabled={isLeftDisabled}
          onClick={handleDefaultClick}
        >
          {leftLabel}
        </Button>
      )}
      {isSlate ? (
        <span
          className={cn(
            'w-px shrink-0 self-stretch bg-slate-100',
            'group-hover:bg-slate-500 group-has-[[data-state=open]]:bg-slate-500',
          )}
          aria-hidden
        />
      ) : (
        <span
          className="w-px shrink-0 self-stretch bg-gray-300"
          aria-hidden
        />
      )}
      <DropdownMenu modal={false}>
        {caretControl}
        <DropdownMenuContent
          align={isSlate ? 'end' : 'start'}
          className="w-auto min-w-36 z-[60]"
        >
          {items.map((item) => (
            <DropdownMenuItem
              key={item.id}
              disabled={disabled || item.disabled}
              onSelect={() => {
                if (disabled || item.disabled) {
                  return;
                }
                item.onSelect?.();
              }}
            >
              {item.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

CompositeMenuButton.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
  })).isRequired,
  defaultItemId: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'slate']),
  label: PropTypes.string,
  caretTooltip: PropTypes.node,
  caretAriaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export { CompositeMenuButton };
